// strategy/baseline.ts - the "sound low-stakes default" (ENGINE_DESIGN.md §6.1). A compact,
// honest decision procedure over hand-class × price × SPR × position, deliberately NOT dressed up as
// GTO (North Star §4.3). It produces the action the exploit rules then adjust - so `recommend()` can
// always show baseline-vs-final and teach the deviation, which is the product.
//
// The procedure is intentionally conservative: it value-bets made hands, calls/bluff-catches by the
// honest pot-odds test (hero equity vs required equity), semi-bluffs strong draws, and otherwise
// checks. It never bluffs air by default - bluffing is a READ-driven deviation the rule layer adds
// (e.g. ROCK_STEAL_SMALL, OVERFOLDER_CBET_ANY), never a baseline habit. That keeps "bluffed a
// station" a gradeable leak rather than something the baseline itself commits.
import { handClassRank } from '@postflop/schema';
/** Made-hand strength at/above which a hand value-bets (mirrors range/bucket.ts VALUE_MIN). */
const VALUE_MIN = handClassRank('top_pair_good_kicker');
/** Made-hand strength at/above which a hand is strong enough to value-raise a bet. */
const STRONG_MIN = handClassRank('two_pair');
/** Required equity to call from pot odds: toCall / (pot + toCall). 0 when hero is not facing a bet. */
export const requiredEquity = (pot, toCall) => toCall > 0 ? toCall / (pot + toCall) : 0;
/**
 * The baseline recommendation for a decision point. Uses only price + hand strength + equity; the
 * exploit-rule layer (§6.2) applies archetype-specific deviations on top.
 */
export const baseline = (ctx) => {
    const r = handClassRank(ctx.hero.handClass);
    const { pot, toCall } = ctx.price;
    // ── facing a bet/raise: continue by strength, bluff-catch by the honest pot-odds test ──────────
    if (toCall > 0) {
        const req = requiredEquity(pot, toCall);
        if (r >= STRONG_MIN) {
            // strong value - raise for value; calling is the conservative alternative.
            return {
                action: 'raise',
                sizingIntent: 'threeQuarters',
                targetStatement: 'raise for value; a strong made hand wants a bigger pot',
                ranked: [
                    { action: 'raise', sizingIntent: 'threeQuarters' },
                    { action: 'call' },
                    { action: 'fold' },
                ],
            };
        }
        if (r >= VALUE_MIN) {
            // a strong one pair - bluff-catch / showdown value: call, don't bloat the pot.
            return {
                action: 'call',
                targetStatement: 'call for showdown value; a strong one pair bluff-catches, it does not raise',
                ranked: [{ action: 'call' }, { action: 'fold' }, { action: 'raise', sizingIntent: 'threeQuarters' }],
            };
        }
        // marginal made hand / draw / air - call only if the price is right, else fold.
        if (ctx.hero.equity >= req) {
            return {
                action: 'call',
                targetStatement: 'call on price; equity clears the pot odds',
                ranked: [{ action: 'call' }, { action: 'fold' }],
            };
        }
        return {
            action: 'fold',
            targetStatement: 'fold; equity does not clear the price',
            ranked: [{ action: 'fold' }, { action: 'call' }],
        };
    }
    // ── not facing a bet (hero can check or bet) ───────────────────────────────────────────────────
    if (r >= VALUE_MIN) {
        return {
            action: 'bet',
            sizingIntent: 'twoThirds',
            targetStatement: 'bet for value; target worse made hands that call',
            ranked: [{ action: 'bet', sizingIntent: 'twoThirds' }, { action: 'check' }],
        };
    }
    if (ctx.hero.handClass === 'strong_draw') {
        return {
            action: 'bet',
            sizingIntent: 'half',
            targetStatement: 'semi-bluff; bet with equity and fold-equity',
            ranked: [{ action: 'bet', sizingIntent: 'half' }, { action: 'check' }],
        };
    }
    // showdown value, weak draws, and air all check by default - no baseline bluffing.
    return {
        action: 'check',
        targetStatement: 'check; realize equity without bloating the pot',
        ranked: [{ action: 'check' }, { action: 'bet', sizingIntent: 'half' }],
    };
};
//# sourceMappingURL=baseline.js.map